import { IArticle } from "@/common/entity/Article";
import { ICorrectResult } from "@/common/entity/CorrectResult";
import { ICorrection } from "@/common/entity/Correction";
import { QueryPageResult } from "@/common/entity/Pagination";
import { Delta } from "@/common/entity/QuillEditor";
import { Response } from "@/common/entity/Response";
import { getUserFrom, IUser, IUserInfo } from "@/common/entity/User";
import { request, UrlPath } from "@/request";

/** 纠错接口超时时间，AI 纠错耗时较长 */
const CORRECT_REQUEST_TIMEOUT = 10 * 60 * 1000;

/** 纠错接口响应格式异常时展示给用户的提示 */
export const MSG_CORRECT_RESP_FORMAT_ERR = "网络繁忙，可再次提交";

/**
 * 文章更新或创建接口参数
 */
export type UpdateParams = {
  /**
   * （可选）文章列表接口返回的文章id，不传时表示是新建文章
   */
  id?: number;
  /**
   * 文章标题，有传id时必须传此参数，空内容时传空字符串
   */
  title?: string;
  /**
   * 文章内容（html格式），有传id时此参数与delta参数至少传一个，空内容时传空字符串
   */
  content?: string;
  /**
   * 表示文章内容的json格式的delta数据(quill编辑器表示内容的数据)，有传id时此参数与content参数至少传一个，空内容时传空字符串
   */
  delta?: Delta;
};

/**
 * 纠错接口和长文本纠错接口参数
 */
export type CorrectParams = {
  /**
   * 要纠错的文本，短文本纠错接口(article/correct)不能超过3000字
   */
  text: string;
  /**
   * （可选）文章列表接口返回的文章id，不传时会新建文章，传时会更新文章内容
   */
  articleId?: number;
  /**
   * 文章标题，有传articleId时必须传此参数，空内容时传空字符串
   */
  title?: string;
  /**
   * 文章内容（html格式），有传articleId时此参数与delta参数至少传一个，空内容时传空字符串
   */
  content?: string;
  /**
   * 表示文章内容的json格式的delta数据(quill编辑器表示内容的数据)，有传articleId时此参数与content参数至少传一个，空内容时传空字符串
   */
  delta?: string;
};

/**
 * 纠错相关接口响应的公共字段
 */
export type CorrectBaseRespData = {
  /**
   * 按段落分组的纠错结果
   */
  result?: ICorrectResult[][];
  /**
   * 剩余纠错次数
   */
  count?: number;
  /**
   * 纠错时创建或更新的文章
   */
  article: IArticle;
  /**
   * 用户信息（旧字段）
   */
  user: IUser;
  /**
   * 用户信息（新字段）
   */
  userInfo?: IUserInfo;
};

/**
 * 纠错接口响应数据
 */
export type CorrectRespData = CorrectBaseRespData;

/**
 * 长文本纠错接口响应数据
 */
export type LongCorrectRespData = CorrectBaseRespData & {
  /**
   * 纠错记录信息
   */
  correction: ICorrection;
  /**
   * 建议开始轮循查询纠错结果要经过的毫秒数
   */
  minFinishTimeMS: number;
};

/**
 * 纠错记录接口响应数据
 */
export type CorrectionRespData = CorrectBaseRespData & {
  /**
   * 纠错记录信息
   */
  correction: ICorrection;
};

type CorrectionInput =
  | ICorrectResult[][]
  | Record<string, ICorrectResult[]>
  | string
  | undefined
  | null;

function isCorrectionMap(
  value: unknown
): value is Record<string, ICorrectResult[]> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 将下标对象（如 {"0": [...], "2": [...]}）转为按段落下标排列的二维数组
 */
function mapCorrectionsToArray(
  correctionMap: Record<string, ICorrectResult[]>
): ICorrectResult[][] {
  const indices = Object.keys(correctionMap)
    .filter((key) => /^\d+$/.test(key))
    .map(Number);
  if (indices.length === 0) return [];

  const maxIndex = Math.max(...indices);
  const result: ICorrectResult[][] = [];
  for (let i = 0; i <= maxIndex; i++) {
    const line = correctionMap[i] ?? correctionMap[String(i)];
    result[i] = Array.isArray(line) ? line : [];
  }
  return result;
}

/**
 * 将接口返回的纠错信息统一为二维数组
 */
export function normalizeCorrections(
  correction: CorrectionInput
): ICorrectResult[][] {
  if (!correction) return [];
  if (typeof correction === "string") {
    try {
      return normalizeCorrections(JSON.parse(correction) as CorrectionInput);
    } catch {
      return [];
    }
  }
  if (Array.isArray(correction)) return correction;
  if (isCorrectionMap(correction)) return mapCorrectionsToArray(correction);
  return [];
}

/**
 * 从纠错接口响应中解析纠错结果
 */
export function resolveCorrectRespCorrections(
  data: Pick<CorrectBaseRespData, "result" | "article"> & {
    correction?: ICorrection;
  }
): ICorrectResult[][] {
  const result = normalizeCorrections(
    data.result as CorrectionInput
  );
  if (result.length) return result;
  const articleCorrection = normalizeCorrections(data.article?.correction);
  if (articleCorrection.length) return articleCorrection;
  const correctionResult = normalizeCorrections(data.correction?.result);
  if (correctionResult.length) return correctionResult;
  return [];
}

/**
 * 从纠错接口响应中解析用户信息
 */
export function resolveCorrectRespUser(
  data: Pick<CorrectBaseRespData, "user" | "userInfo">
): IUser | null {
  if (data.user) return data.user;
  if (data.userInfo) return getUserFrom(data.userInfo);
  return null;
}

function throwCorrectFormatErr(devMsg: string): never {
  throw Response.newForException(MSG_CORRECT_RESP_FORMAT_ERR, devMsg);
}

/**
 * 校验短文本纠错接口响应结构
 */
export function validateCorrectRespData(
  data: CorrectRespData | null | undefined
): CorrectRespData {
  if (!data || typeof data !== "object") {
    throwCorrectFormatErr("纠错响应数据为空");
  }
  if (!data.article || typeof data.article !== "object") {
    throwCorrectFormatErr("纠错响应缺少 article 字段");
  }
  return data;
}

/**
 * 校验长文本纠错接口响应结构
 */
export function validateLongCorrectRespData(
  data: LongCorrectRespData | null | undefined
): LongCorrectRespData {
  const validData = validateCorrectRespData(data) as LongCorrectRespData;
  if (!validData.correction || typeof validData.correction !== "object") {
    throwCorrectFormatErr("长文本纠错响应缺少 correction 字段");
  }
  if (!validData.correction.id) {
    throwCorrectFormatErr("长文本纠错响应 correction.id 为空");
  }
  return validData;
}

/**
 * 校验纠错记录查询接口响应结构
 */
export function validateCorrectionQueryRespData(
  data: CorrectionRespData | null | undefined
): CorrectionRespData {
  const validData = validateCorrectRespData(data) as CorrectionRespData;
  if (!validData.correction || typeof validData.correction !== "object") {
    throwCorrectFormatErr("纠错记录响应缺少 correction 字段");
  }
  if (typeof validData.correction.status === "undefined") {
    throwCorrectFormatErr("纠错记录响应 correction.status 无效");
  }
  return validData;
}

export type CountCorrectRespData = {
  count?: number;
  failText?: string;
};

/**
 * 获取范文文章接口
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function defaults(isShowToast: boolean = true): Promise<IArticle[]> {
  return request<IArticle[]>(UrlPath.defaultArticles, isShowToast);
}

/**
 * 获取文章接口
 * @param id 文章id
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function query(id: number, isShowToast: boolean = true): Promise<IArticle> {
  return request<IArticle>({ url: UrlPath.article, data: { id } }, isShowToast);
}

/**
 * 保存或创建文章接口
 * @param params 请求参数
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function save(
  params: UpdateParams,
  isShowToast: boolean = true
): Promise<IArticle> {
  return request<IArticle>(
    { url: UrlPath.updateArticle, data: params },
    isShowToast
  );
}

/**
 * 纠错接口
 * @param params 请求参数
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function correct(
  params: CorrectParams,
  isShowToast: boolean = true
): Promise<CorrectRespData> {
  return request<CorrectRespData>(
    { url: UrlPath.correct, data: params, timeout: CORRECT_REQUEST_TIMEOUT },
    isShowToast
  );
}

/**
 * 长文本纠错接口
 * @param params 请求参数
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function longCorrect(
  params: CorrectParams,
  isShowToast: boolean = true
): Promise<LongCorrectRespData> {
  return request<LongCorrectRespData>(
    {
      url: UrlPath.longCorrect,
      data: params,
      timeout: CORRECT_REQUEST_TIMEOUT,
    },
    isShowToast
  );
}

/**
 * 获取id为{@link correctionId}的纠错记录
 * @param correctionId 纠错记录id
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 请求响应
 */
function correction(
  correctionId: number,
  isShowToast: boolean = true
): Promise<CorrectionRespData> {
  return request<CorrectionRespData>(
    { url: UrlPath.correction, data: { correctionId } },
    isShowToast
  );
}

/**
 * 请求计算文本{@link text}会消耗的纠错次数
 */
function countCorrect(
  text: string,
  isShowToast: boolean = true
): Promise<CountCorrectRespData> {
  return request<CountCorrectRespData>(
    { url: UrlPath.countCorrect, data: { text } },
    isShowToast
  );
}

/**
 * 请求纠错列表
 * @param pageNum 页码
 * @param pageSize 页面大小
 */
function list(
  pageNum: number,
  pageSize: number = 10,
  isShowToast: boolean = true
): Promise<QueryPageResult<IArticle[]>> {
  return request<QueryPageResult<IArticle[]>>(
    {
      url: "/article/list-v2",
      data: { pageNum, pageSize },
    },
    isShowToast
  );
}

/**
 * 删除纠错记录
 * @param id 要删除的记录id
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 */
function del(id: number, isShowToast: boolean = true): Promise<IArticle> {
  return request<IArticle>(
    { url: "/article/delete", data: { id } },
    isShowToast
  );
}

export const ArticleApis = {
  defaults,
  query,
  save,
  correct,
  longCorrect,
  correction,
  countCorrect,
  list,
  del,
};
